// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { resultCountProps } from "@store/Themes/common/props/resultCount";
import { tableGetEllipsisCellProps, tableProps } from "@store/Themes/common/props/table";
import constants from "@store/constants";
import { useInvoicePaymentContext } from "@store/Framework/Components/InvoicePaymentContext/InvoicePaymentContext";
import InvoiceDetailPageTypeLink from "@store/Overrides/Components/InvoiceDetailPageTypeLink";
import * as React from "react";
import { FC } from "react";

const enum fields {
    showInvoiceNumber = "showInvoiceNumber",
    showInvoiceDate = "showInvoiceDate",
    showDueDate = "showDueDate",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showStatus = "showStatus",
    showInvoiceTotal = "showInvoiceTotal",
    showCurrentBalance = "showCurrentBalance",
    showPaidAmount = "showPaidAmount",
    showPrint = "showPrint",
}

export interface InvoicePaymentInvoiceListProps extends WidgetProps {
    fields: {
        [fields.showInvoiceNumber]: boolean;
        [fields.showInvoiceDate]: boolean;
        [fields.showDueDate]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showStatus]: boolean;
        [fields.showInvoiceTotal]: boolean;
        [fields.showCurrentBalance]: boolean;
        [fields.showPaidAmount]: boolean;
        [fields.showPrint]: boolean;
    };
}

export interface InvoicePaymentInvoiceListStyles {
    container?: InjectableCss;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    invoiceNumberHeader?: DataTableHeaderProps;
    invoiceDateHeader?: DataTableHeaderProps;
    dueDateHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    poNumberHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    totalHeader?: DataTableHeaderProps;
    currentBalanceHeader?: DataTableHeaderProps;
    invoiceNumberCell?: DataTableCellProps;
    invoiceDateCell?: DataTableCellProps;
    dueDateCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    poNumberCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    totalCell?: DataTableCellProps;
    currentBalanceCell?: DataTableCellProps;
    countWrapper?: InjectableCss;
    countText?: TypographyProps;
    tableWrapper?: InjectableCss;
}

export const tableStyles: InvoicePaymentInvoiceListStyles = {
    countWrapper: resultCountProps.wrapper,
    countText: resultCountProps.countText,
    tableWrapper: tableProps.wrapper,
    currentBalanceHeader: tableProps.amountHeader,
    currentBalanceCell: tableProps.amountCell,
    dataTable: tableProps.dataTable,
    totalHeader: tableProps.amountHeader,
    totalCell: tableProps.amountCell,
    shipToHeader: tableProps.tightHeader,
    shipToCell: tableGetEllipsisCellProps(170),
    statusCell: tableGetEllipsisCellProps(95),
    poNumberCell: tableGetEllipsisCellProps(95),
};

const styles = tableStyles;

const InvoicePaymentInvoiceList: FC<InvoicePaymentInvoiceListProps> = ({
    fields: {
        showInvoiceNumber,
        showInvoiceDate,
        showDueDate,
        showShipTo,
        showPONumber,
        showStatus,
        showInvoiceTotal,
        showCurrentBalance,
        showPaidAmount,
        showPrint,
    },
}) => {
    const { invoices } = useInvoicePaymentContext();
    const language = useAppSelector(state => state.context.session.language);

    if (!invoices.length) {
        return null;
    }

    const invoicesCount = invoices.length;
    const invoicesCountLabel = invoicesCount === 1 ? translate("Invoice") : translate("Invoices");

    const rows = invoices.map(invoice => {
        return {
            id: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            date: invoice.invoiceDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.invoiceDate),
                      language,
                      options: {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                      },
                  })
                : "",
            dueDate: invoice.dueDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.dueDate),
                      language,
                      options: {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                      },
                  })
                : "",
            shipTo: `${invoice.stCompanyName} ${invoice.btAddress1} ${invoice.btAddress2} ${invoice.shipToCity} ${invoice.shipToState}`,
            customerPO: invoice.customerPO,
            status: invoice.status,
            total: invoice.invoiceTotalDisplay,
            currentBalance: invoice.currentBalanceDisplay,
            invoiceUniqueNumber: invoice.invoiceUniqueNumber || "",
            invoiceSequence: invoice.invoiceSequence?.toString() || "",
            paidAmountDisplay: invoice.paidAmountDisplay || "",
            canViewDetails: invoice.canViewDetails,
        };
    });

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.countWrapper}>
                <Typography {...styles.countText}>
                    {invoicesCount} {invoicesCountLabel}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.tableWrapper}>
                <DataTable {...styles.dataTable}>
                    <DataTableHead {...styles.dataTableHead}>
                        {showInvoiceNumber && (
                            <DataTableHeader {...styles.invoiceNumberHeader}>
                                {translate("grimco_invoiceHistory_table_number")}
                            </DataTableHeader>
                        )}
                        {showInvoiceDate && (
                            <DataTableHeader {...styles.invoiceDateHeader}>
                                {translate("grimco_invoiceHistory_table_issuedDate")}
                            </DataTableHeader>
                        )}
                        {showDueDate && (
                            <DataTableHeader {...styles.dueDateHeader}>
                                {translate("grimco_invoiceHistory_table_dueDate")}
                            </DataTableHeader>
                        )}
                        {showShipTo && (
                            <DataTableHeader {...styles.shipToHeader}>{translate("Ship To / Pick Up")}</DataTableHeader>
                        )}
                        {showPONumber && (
                            <DataTableHeader {...styles.poNumberHeader}>{translate("PO #")}</DataTableHeader>
                        )}
                        {showStatus && (
                            <DataTableHeader {...styles.statusHeader}>{translate("Status")}</DataTableHeader>
                        )}
                        {showInvoiceTotal && (
                            <DataTableHeader {...styles.totalHeader}>
                                {translate("grimco_invoiceHistory_table_invoicedAmount")}
                            </DataTableHeader>
                        )}
                        {showPaidAmount && (
                            <DataTableHeader {...styles.totalHeader}>
                                {translate("grimco_invoiceHistory_table_creditAmountPaid")}
                            </DataTableHeader>
                        )}
                        {showCurrentBalance && (
                            <DataTableHeader {...styles.currentBalanceHeader}>
                                {translate("grimco_invoiceHistory_table_dueAmount")}
                            </DataTableHeader>
                        )}
                        {showPrint && <DataTableHeader tight title={translate("Print")} />}
                    </DataTableHead>
                    <DataTableBody {...styles.dataTableBody}>
                        {rows?.map(
                            ({
                                id,
                                invoiceNumber,
                                date,
                                dueDate,
                                shipTo,
                                customerPO,
                                status,
                                total,
                                currentBalance,
                                paidAmountDisplay,
                                invoiceUniqueNumber,
                                invoiceSequence,
                                canViewDetails,
                            }) => (
                                <DataTableRow key={id} {...styles.dataTableRow}>
                                    {showInvoiceNumber && (
                                        <DataTableCell {...styles.invoiceNumberCell}>
                                            {canViewDetails ? (
                                                <InvoiceDetailPageTypeLink
                                                    title={invoiceUniqueNumber} // Store Custom Code
                                                    invoiceNumber={invoiceNumber}
                                                    invoiceSuffix={invoiceSequence} // Store Custom Code
                                                />
                                            ) : (
                                                invoiceUniqueNumber
                                            )}
                                        </DataTableCell>
                                    )}
                                    {showInvoiceDate && (
                                        <DataTableCell {...styles.invoiceDateCell}>{date}</DataTableCell>
                                    )}
                                    {showDueDate && <DataTableCell {...styles.dueDateCell}>{dueDate}</DataTableCell>}
                                    {showShipTo && <DataTableCell {...styles.shipToCell}>{shipTo}</DataTableCell>}
                                    {showPONumber && (
                                        <DataTableCell {...styles.poNumberCell}>{customerPO}</DataTableCell>
                                    )}
                                    {showStatus && <DataTableCell {...styles.statusCell}>{status}</DataTableCell>}
                                    {showInvoiceTotal && <DataTableCell {...styles.totalCell}>{total}</DataTableCell>}
                                    {showPaidAmount && (
                                        <DataTableCell {...styles.totalCell}>{paidAmountDisplay}</DataTableCell>
                                    )}
                                    {showCurrentBalance && (
                                        <DataTableCell {...styles.currentBalanceCell}>{currentBalance}</DataTableCell>
                                    )}
                                    {showPrint && (
                                        <DataTableCell>
                                            <InvoiceDetailPageTypeLink
                                                title={invoiceUniqueNumber}
                                                invoiceNumber={invoiceNumber}
                                                invoiceSuffix={invoiceSequence}
                                                print={true}
                                            />
                                        </DataTableCell>
                                    )}
                                </DataTableRow>
                            ),
                        )}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InvoicePaymentInvoiceList,
    definition: {
        group: constants.widgetGroup.invoicePayment,
        displayName: "Invoice List",
        allowedContexts: [constants.pageType.invoicePayment],
        fieldDefinitions: [
            {
                name: fields.showInvoiceNumber,
                displayName: "Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
            },
            {
                name: fields.showInvoiceDate,
                displayName: "Issue Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showDueDate,
                displayName: "Due Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showInvoiceTotal,
                displayName: "Invoiced Amount",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showPaidAmount,
                displayName: "Paid Amount",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showCurrentBalance,
                displayName: "Due Amount",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showPrint,
                displayName: "Print",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
